import { HTMLAttributes } from 'react';
import { AvatarSize } from '../../enums/Avatar';
import { joinClassNames } from '../../utils';
import { BaseIcon } from '../Graphics/Icons/BaseIcon';
import styles from './Avatar.module.scss';

type AvatarProps = HTMLAttributes<SVGElement> & {
  /**
   * Sizes:
   * - AvatarSize.Table = 36px,
   * - AvatarSize.Network = 48px,
   * - AvatarSize.Profile = 64px.
   */
  size?: AvatarSize;
  /**
   * User id
   */
  userId: number;
  /**
   * Person's first name
   */
  firstName: string;
  /**
   * Person's last name
   */
  lastName: string;
  /**
   * Profile image url
   */
  profileImage?: string;
};

const sizeStyles = Object.freeze({
  [AvatarSize.Table]: '',
  [AvatarSize.Network]: styles.avatarNetwork,
  [AvatarSize.Profile]: styles.avatarProfile,
});

export const Avatar = ({
  size = AvatarSize.Table,
  userId,
  firstName,
  lastName,
  profileImage,
  className,
  ...props
}: AvatarProps) => {
  const sizeClassName: string = sizeStyles[size];
  const classNames = joinClassNames(styles.avatar, sizeClassName, className);
  const radius = size / 2;
  const profileId = `avatar_${userId}_${size}`;
  const firstInitial = firstName[0]?.toLocaleUpperCase() ?? '';
  const lastInitial = lastName[0]?.toLocaleUpperCase() ?? '';
  const initials = firstInitial || lastInitial ? `${firstInitial}${lastInitial}` : '??';

  return (
    <BaseIcon
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      {...props}
      className={classNames}
      title={initials}
      desc={`${firstName} ${lastName}`}
    >
      {profileImage ? (
        <g>
          <defs>
            <pattern id={profileId} x="0" y="0" patternUnits="userSpaceOnUse" height={size} width={size}>
              <image x="0" y="0" xlinkHref={profileImage} width={size} height={size} />
            </pattern>
          </defs>
          <circle cx={radius} cy={radius} r={radius} fill={`url(#${profileId})`} />
        </g>
      ) : (
        <g>
          <circle cx={radius} cy={radius} r={radius} />
          <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central">
            {initials}
          </text>
        </g>
      )}
    </BaseIcon>
  );
};

Avatar.displayName = 'Avatar';
